/**
 * Role-Based Navigation Utility
 * Centralized logic for determining dashboard routes based on user roles
 */

/**
 * Get the appropriate dashboard route based on user role
 * @param {string} userRole - The user's role
 * @param {object} [user] - Optional user object containing onboarding status
 * @returns {string} - The dashboard route path
 */
export const getDashboardRoute = (userRole, user = null) => {
  switch (userRole) {
    case 'SUPER_ADMIN':
    case 'SUPER_ADMIN_USER':
      return '/admin';
    
    case 'HOSPITAL_USER': {
      // Check onboarding lifecycle status if available on the user object
      const onboardingStatus = user?.hospital?.onboarding_status || user?.onboarding_status;
      if (onboardingStatus === 'SUBMITTED') {
        return '/hospital/onboarding/submitted';
      }
      if (onboardingStatus === 'NOT_STARTED' || onboardingStatus === 'IN_PROGRESS') {
        return '/hospital/onboarding';
      }
      return '/hospital';
    }
    
    case 'TENANT_ADMIN':
    case 'TENANT_USER':
    case 'FLEET_ADMIN':
      return '/fleet';

    case 'COMMAND_CENTRE_USER':
      return '/command-center';

    case 'ONBOARD_USER':
    case 'onboard_user':
      return '/hospital/register';

    // Any role without a dashboard yet (e.g. PARTNERSHIP_MANAGER) lands on a
    // clear "no dashboard assigned" page instead of looping — /dashboard used
    // to route back into RoleBasedRedirect, which calls getDashboardRoute
    // again, which returned /dashboard again: an infinite redirect loop for
    // any unmapped role.
    default:
      return '/no-dashboard-access';
  }
};

/**
 * Get the dashboard name for display purposes
 * @param {string} userRole - The user's role
 * @returns {string} - The dashboard name
 */
export const getDashboardName = (userRole) => {
  switch (userRole) {
    case 'SUPER_ADMIN':
    case 'SUPER_ADMIN_USER':
      return 'Super Admin Dashboard';
    
    case 'HOSPITAL_USER':
      return 'Hospital Dashboard';
    
    case 'TENANT_ADMIN':
    case 'TENANT_USER':
    case 'FLEET_ADMIN':
      return 'Fleet Dashboard';

    case 'COMMAND_CENTRE_USER':
      return 'Command Center Dashboard';

    case 'ONBOARD_USER':
    case 'onboard_user':
      return 'Partner Onboarding';

    default:
      return 'No Dashboard Assigned';
  }
};

/**
 * Check if user has access to a specific route
 * @param {string} userRole - The user's role
 * @param {string} route - The route to check
 * @returns {boolean} - Whether the user has access
 */
export const hasRouteAccess = (userRole, route) => {
  const onboardingRoutes = ['/hospital/register', '/hospital/partner-register', '/hospital/onboarding', '/hospital/onboarding/manual', '/hospital/onboarding/voice', '/hospital/onboarding/submitted'];
  const allowedRoutes = {
    'SUPER_ADMIN': ['/admin', '/hospital', '/fleet', '/analytics', ...onboardingRoutes],
    'SUPER_ADMIN_USER': ['/admin', '/hospital', '/fleet', '/analytics', ...onboardingRoutes],
    'HOSPITAL_USER': ['/hospital', ...onboardingRoutes],
    'TENANT_ADMIN': ['/fleet', ...onboardingRoutes],
    'TENANT_USER': ['/fleet', ...onboardingRoutes],
    'FLEET_ADMIN': ['/fleet', ...onboardingRoutes],
    'COMMAND_CENTRE_USER': ['/command-center'],
    'ONBOARD_USER': onboardingRoutes,
    'onboard_user': onboardingRoutes
  };

  return allowedRoutes[userRole]?.includes(route) || false;
};

/**
 * Get all accessible routes for a user role
 * @param {string} userRole - The user's role
 * @returns {string[]} - Array of accessible routes
 */
export const getAccessibleRoutes = (userRole) => {
  const onboardingRoutes = ['/hospital/register', '/hospital/partner-register', '/hospital/onboarding', '/hospital/onboarding/manual', '/hospital/onboarding/voice', '/hospital/onboarding/submitted'];
  const routeMap = {
    'SUPER_ADMIN': ['/admin', '/hospital', '/fleet', '/analytics', ...onboardingRoutes],
    'SUPER_ADMIN_USER': ['/admin', '/hospital', '/fleet', '/analytics', ...onboardingRoutes],
    'HOSPITAL_USER': ['/hospital', ...onboardingRoutes],
    'TENANT_ADMIN': ['/fleet', ...onboardingRoutes],
    'TENANT_USER': ['/fleet', ...onboardingRoutes],
    'FLEET_ADMIN': ['/fleet', ...onboardingRoutes],
    'COMMAND_CENTRE_USER': ['/command-center'],
    'ONBOARD_USER': onboardingRoutes,
    'onboard_user': onboardingRoutes
  };

  return routeMap[userRole] || [];
};

